import type { FC } from 'react';

import { FEATURE_TOGGLES } from '@/features/settings/constants';
import type { EditableFeatureFlags } from '@/types/settings.types';
import { cn } from '@/utils/cn.util';

interface FeatureTogglesPanelProps {
  flags: EditableFeatureFlags;
  onToggle: (flag: keyof EditableFeatureFlags) => void;
}

/** Ports the prototype's `.toggle`: 42×24 track, 18px knob, travelling from left 3px to 21px. */
const Toggle: FC<{ on: boolean; onClick: () => void; label: string }> = ({ on, onClick, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    onClick={onClick}
    className={cn(
      'relative h-6 w-[42px] shrink-0 rounded-xl transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
      on ? 'bg-brand-500' : 'bg-[var(--color-surface-muted-2)]',
    )}
  >
    <span
      className={cn(
        'absolute top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow-[0_1px_3px_rgba(20,40,90,0.2)] transition-all',
        on ? 'left-[21px]' : 'left-[3px]',
      )}
    />
  </button>
);

const FeatureTogglesPanel: FC<FeatureTogglesPanelProps> = ({ flags, onToggle }) => (
  <section className="rounded-2xl border border-[var(--color-divider)] bg-white p-7 shadow-[var(--shadow-card)]">
    <h2 className="mb-4 text-[13px] font-extrabold text-[var(--color-ink)]">⚙️ Feature Toggles</h2>

    {FEATURE_TOGGLES.map((toggle) => (
      <div
        key={toggle.key}
        className="flex items-center justify-between gap-6 border-b border-[var(--color-divider)] py-4 last:border-b-0"
      >
        <div>
          <div className="text-[13.5px] font-semibold text-[var(--color-ink)]">{toggle.name}</div>
          <div className="mt-0.5 text-[12px] text-[var(--color-ink-muted)]">
            {toggle.description}
          </div>
        </div>
        <Toggle on={flags[toggle.key]} onClick={() => onToggle(toggle.key)} label={toggle.name} />
      </div>
    ))}

    <p className="mt-4 text-[12px] text-[var(--color-ink-muted)]">
      Each switch gates real server behaviour and takes effect as soon as you save.
    </p>
  </section>
);

export default FeatureTogglesPanel;
